import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
  TextField,
  Stack,
  Snackbar,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, 
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, AreaChart, Area 
} from 'recharts';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DateRangeIcon from '@mui/icons-material/DateRange';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Chart from 'chart.js/auto';
import { SettingsContext } from '../../contexts/SettingsContext';

// Import hooks from dataService for better integration
import {
  useReservationsData,
  useClientsData,
  usePackagesData, 
  useFinancialData,
  useSuppliersData
} from '../../services/dataService';

const ReservationsReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings, formatCurrency, formatDate } = useContext(SettingsContext);
  const [currentTab, setCurrentTab] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Add a reference to the report content for PDF export
  const reportRef = React.useRef(null);
  
  // Get data from shared hooks for interconnection with other parts
  const { reservations, loading: reservationsLoading, stats: reservationStats } = useReservationsData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { transactions, loading: financialLoading } = useFinancialData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData({ skipFinancial: true });
  
  // Combined loading state
  const isLoading = reservationsLoading || clientsLoading || packagesLoading || financialLoading || suppliersLoading;

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (isLoading) {
      const safetyTimeout = setTimeout(() => {
        console.log('Safety timeout triggered to prevent infinite loading in ReservationsReports');
        // Force loading state to false after timeout
        // This doesn't affect the actual data loading, just prevents UI from being stuck
        // We need to dispatch an event that the dataService hooks will listen for
        window.dispatchEvent(new CustomEvent('forceLoadingComplete', {
          detail: { component: 'ReservationsReports' }
        }));
      }, 5000); // 5 seconds timeout
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [isLoading]);

  // Destinations list extracted from reservations for filtering
  const [destinations, setDestinations] = useState([]);
  
  useEffect(() => {
    // Extract unique destinations when reservations data changes
    if (reservations && reservations.length > 0) {
      const uniqueDestinations = [...new Set(reservations.map(r => r.destination))].filter(Boolean);
      setDestinations(uniqueDestinations);
    }
  }, [reservations]);

  // COLORS for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  const STATUS_COLORS = {
    'Confirmée': '#10b981',
    'En attente': '#f59e0b',
    'Annulée': '#ef4444'
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePeriodChange = (event) => {
    setPeriodFilter(event.target.value);
    // Reset date filters when changing period
    if (event.target.value !== 'custom') {
      setDateFilterEnabled(false);
      setCustomStartDate('');
      setCustomEndDate('');
    } else {
      setDateFilterEnabled(true);
    }
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleDestinationChange = (event) => {
    setDestinationFilter(event.target.value);
  };

  const handleCustomDateFilter = () => {
    if (customStartDate && customEndDate) {
      setStartDate(new Date(customStartDate));
      setEndDate(new Date(customEndDate));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    
    // Clear any cached PDF data to ensure we use real-time data
    window.localStorage.removeItem('pdfExportCache');
    
    try {
      // Create a new PDF document with professional settings
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const accentColor = [5, 150, 105]; // green
      const warningColor = [245, 158, 11]; // amber
      const dangerColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      const lightTextColor = [107, 114, 128]; // medium gray
      const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#1e40af', '#7c3aed', '#059669'];
      
      // Improved chart rendering function using Chart.js
      const createChartImage = (chartType, data, options = {}) => {
        return new Promise((resolve) => {
          try {
            // Skip if no data
            if (!data || !Array.isArray(data) || data.length === 0) {
              console.log('No data for chart image, falling back to table');
              resolve(null);
              return;
            }
            
            // Create a canvas element with larger dimensions for better rendering
            const canvas = document.createElement('canvas');
            canvas.width = options.width || 600; // Increased width
            canvas.height = options.height || 400; // Increased height
            canvas.style.backgroundColor = '#ffffff';
            
            // Temporarily add to document to ensure it renders correctly
            canvas.style.position = 'absolute';
            canvas.style.top = '-9999px';
            canvas.style.zIndex = '-1'; // Make sure it's not visible
            document.body.appendChild(canvas);
            
            const ctx = canvas.getContext('2d');
            
            if (chartType === 'pie') {
              // Create pie chart with Chart.js - with improved rendering
              new Chart(ctx, {
                type: 'pie',
                data: {
                  labels: data.map(item => item.name || item.label || ''),
                  datasets: [{
                    data: data.map(item => item.value || 0),
                    backgroundColor: CHART_COLORS,
                    borderWidth: 1
                  }]
                },
                options: {
                  responsive: false,
                  maintainAspectRatio: false,
                  layout: {
                    padding: {
                      left: 10,
                      right: 10,
                      top: 10,
                      bottom: 10
                    }
                  },
                  plugins: {
                    legend: { 
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        font: {
                          size: 9
                        }
                      }
                    }
                  }
                }
              });
            } else if (chartType === 'bar') {
              // Check if this is the monthly data with multiple series
              const hasMultipleSeries = data.length > 0 && 
                ('confirmées' in data[0] || 'enAttente' in data[0] || 'annulées' in data[0]);
                
              if (hasMultipleSeries) {
                // Create multi-series bar chart for monthly data
                new Chart(ctx, {
                  type: 'bar',
                  data: {
                    labels: data.map(item => item.name || ''),
                    datasets: [
                      {
                        label: 'Confirmées',
                        data: data.map(item => item.confirmées || 0),
                        backgroundColor: '#10b981', // green
                        borderWidth: 1
                      },
                      {
                        label: 'En Attente',
                        data: data.map(item => item.enAttente || 0),
                        backgroundColor: '#f59e0b', // amber
                        borderWidth: 1
                      },
                      {
                        label: 'Annulées',
                        data: data.map(item => item.annulées || 0),
                        backgroundColor: '#ef4444', // red
                        borderWidth: 1
                      }
                    ]
                  },
                  options: {
                    responsive: false,
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }
                });
              } else {
                // Standard bar chart for regular data
                new Chart(ctx, {
                  type: 'bar',
                  data: {
                    labels: data.map(item => item.name || ''),
                    datasets: [{
                      label: 'Valeur',
                      data: data.map(item => item.value || 0),
                      backgroundColor: CHART_COLORS[0],
                      borderWidth: 1
                    }]
                  },
                  options: {
                    responsive: false,
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }
                });
              }
            } else if (chartType === 'line' || chartType === 'area') {
              // Create line/area chart with Chart.js
              new Chart(ctx, {
                type: 'line',
                data: {
                  labels: data.map(item => item.name || ''),
                  datasets: [{
                    label: 'Revenus',
                    data: data.map(item => item.revenus || 0),
                    borderColor: CHART_COLORS[0],
                    backgroundColor: 'rgba(136, 132, 216, 0.2)',
                    borderWidth: 2,
                    fill: true // Always fill for better visibility in PDF
                  }]
                },
                options: {
                  responsive: false,
                  maintainAspectRatio: false,
                  scales: {
                    y: { 
                      beginAtZero: true,
                      ticks: {
                        // Format large numbers with k suffix for better readability
                        callback: function(value) {
                          if (value >= 1000) {
                            return (value / 1000) + 'k';
                          }
                          return value;
                        }
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top'
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          let value = context.raw;
                          // Format large numbers with spaces for thousands
                          return new Intl.NumberFormat('fr-FR', { 
                            style: 'currency', 
                            currency: 'EUR',
                            maximumFractionDigits: 0
                          }).format(value);
                        }
                      }
                    }
                  }
                }
              });
            }
            
            // Convert to image with longer timeout for complete rendering
            setTimeout(() => {
              try {
                console.log('Capturing chart as image...');
                const dataURL = canvas.toDataURL('image/png');
                document.body.removeChild(canvas);
                console.log('Chart image created successfully');
                resolve(dataURL);
              } catch (err) {
                console.error('Error converting chart to image:', err);
                document.body.removeChild(canvas);
                resolve(null);
              }
            }, 800); // Longer timeout to ensure complete rendering
            
          } catch (err) {
            console.error('Error creating chart:', err);
            resolve(null);
          }
        });
      };
      
      // Helper function to convert chart data to table data
      const createTableFromChartData = (chartType, data) => {
        // Create table data that can be rendered directly in the PDF
        if (!data || data.length === 0) return null;
        
        // Extract data in a format suitable for tables
        const headers = ["Catégorie", "Valeur"];
        const rows = data.map(item => {
          const label = item.name || item.label || '';
          const value = typeof item.value !== 'undefined' ? item.value : 
                      (typeof item.total !== 'undefined' ? item.total : 0);
          return [label, typeof value === 'number' ? value.toLocaleString() : value];
        });
        
        return { headers, rows };
      };
      
      // Add header with logo and title
      const addHeader = (title, subtitle) => {
        // Draw top header bar with gradient
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .rect(0, 0, pageWidth, 25, 'F');
          
        // Add agency logo and name
        if (settings.agencyLogo) {
          try {
            // Add the agency logo from base64 string
            doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
            
            // Add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
          } catch (logoError) {
            console.error('Error adding logo to PDF:', logoError);
            // Fallback to text only
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
        } else {
          // No logo, just add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
          
        // Add title and subtitle
        doc.setTextColor(255, 255, 255)
          .setFontSize(16)
          .setFont('helvetica', 'bold')
          .text(title, pageWidth - margin, 17, { align: 'right' });
          
        // Add white separation line
        doc.setDrawColor(255, 255, 255)
          .setLineWidth(0.5)
          .line(margin, 25, pageWidth - margin, 25);
          
        // Add subtitle and date
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFontSize(12)
          .setFont('helvetica', 'normal')
          .text(subtitle, margin, 40);
          
        doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFontSize(10)
          .text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, 40, { align: 'right' });
          
        // Add filter information
        doc.setFontSize(9)
          .text(`Filtres: Période: ${
            periodFilter === 'month' ? 'Mois courant' : 
            periodFilter === 'quarter' ? 'Trimestre courant' : 
            periodFilter === 'year' ? 'Année courante' : 
            periodFilter === 'lastmonth' ? 'Mois précédent' : 
            periodFilter === 'custom' ? 'Personnalisée' : 'Toutes'
          } | Statut: ${statusFilter === 'all' ? 'Tous' : statusFilter} | Destination: ${destinationFilter === 'all' ? 'Toutes' : destinationFilter}`, 
          margin, 48);
              
        // Add decorative line
        doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
          .setLineWidth(0.5)
          .line(margin, 55, pageWidth - margin, 55);
          
        return 60; // Return Y position after header
      };
      
      // Add footer to all pages
      const addFooter = (pageNum) => {
        const totalPages = doc.internal.getNumberOfPages();
        
        // Position the footer properly at the bottom of the page
        const footerY = pageHeight - 15;
        
        // Add separator line
        doc.setDrawColor(200, 200, 200)
          .setLineWidth(0.5)
          .line(margin, footerY - 5, pageWidth - margin, footerY - 5);
          
        // Left side: TravelIT name and page numbers
        doc.setFontSize(8)
          .setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFont('helvetica', 'normal')
          .text(`TravelIT - Système de Gestion pour Agence de Voyage | Page ${pageNum} sur ${totalPages}`, 
                margin, footerY);
                
        // Right side: Generation date
        doc.setFontSize(8)
          .text(`Généré le: ${new Date().toLocaleDateString()}`, 
                pageWidth - margin, footerY, { align: 'right' });
      };
      
      // Add a section heading
      const addSection = (title, yPos, icon = null) => {
        // Add section heading with accent color background
        doc.setFillColor(245, 247, 250)  // very light blue background
          .roundedRect(margin - 5, yPos - 5, contentWidth + 10, 12, 2, 2, 'F');
          
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setFontSize(11)
          .setFont('helvetica', 'bold')
          .text(title, margin, yPos + 3);
          
        return yPos + 12;  // Reduced from 15 to 12 to save space
      };
      
      // Add KPI cards in a row
      const addKpiRow = (kpis, yPos) => {
        const cardWidth = contentWidth / kpis.length - 5;
        const cardHeight = 30; // Reduced from 35 to 30
        const colors = [primaryColor, accentColor, warningColor, dangerColor]; // blue, green, amber, red
        
        kpis.forEach((kpi, index) => {
          const xPos = margin + (index * (cardWidth + 5));
          const color = colors[index % colors.length];
          
          // Card background
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(xPos, yPos, cardWidth, cardHeight, 2, 2, 'FD');
            
          // Value
          doc.setTextColor(color[0], color[1], color[2])
            .setFontSize(12) // Reduced from 14 to 12
            .setFont('helvetica', 'bold');
            
          // Make sure the text fits in the card width
          const valueText = kpi.value;
          const textWidth = doc.getStringUnitWidth(valueText) * 12 / doc.internal.scaleFactor;
          
          if (textWidth > cardWidth - 10) {
            // If the text is too wide, use a smaller font size
            doc.setFontSize(9);
          }
            
          doc.text(valueText, xPos + cardWidth/2, yPos + 13, { align: 'center' }); // Adjusted from 15 to 13
            
          // Title
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(8)
            .setFont('helvetica', 'normal')
            .text(kpi.title, xPos + cardWidth/2, yPos + 22, { align: 'center' }); // Adjusted from 25 to 22
            
          // Evolution
          if (kpi.change) {
            const isPositive = !kpi.change.includes('-');
            const changeColor = isPositive ? [5, 150, 105] : [220, 38, 38]; // green or red
            
            doc.setTextColor(changeColor[0], changeColor[1], changeColor[2])
              .setFontSize(7)
              .setFont('helvetica', 'bold')
              .text(kpi.change, xPos + cardWidth/2, yPos + 28, { align: 'center' }); // Adjusted from 32 to 28
          }
        });
        
        return yPos + cardHeight + 8; // Reduced from 10 to 8
      };
      
      // Add chart to PDF with improved rendering
      const addChartToPDF = async (chartType, data, title, yPos, height = 120) => {
        const footerSpace = 30; // Reserve space for footer
        
        try {
          // First check if we need to start a new page for this chart
          const estimatedHeight = 12 + height + 30; // title + chart height + some padding
          if (yPos + estimatedHeight > pageHeight - footerSpace) {
            doc.addPage();
            addFooter(doc.internal.getNumberOfPages());
            yPos = margin + 10;
          }
          
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(10)
            .setFont('helvetica', 'bold')
            .text(title, margin, yPos);
          
          yPos += 12;
          
          // First try to generate a chart image
          const chartImage = await createChartImage(chartType, data, { width: 600, height: 400 });
          
          if (chartImage) {
            // We have a chart image, add it to the PDF
            console.log('Adding chart image to PDF');
            
            // Calculate image dimensions to fit content width while maintaining aspect ratio
            const imgProps = doc.getImageProperties(chartImage);
            const imgWidth = contentWidth;
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            
            try {
              // Add the image
              doc.addImage(chartImage, 'PNG', margin, yPos, imgWidth, imgHeight);
              return yPos + imgHeight + 15;
            } catch (imgError) {
              console.error('Error adding chart image to PDF:', imgError);
              // If image fails, fall back to table
            }
          }
          
          // Fall back to table if image generation fails
          console.log('Falling back to table for chart data');
          const tableData = createTableFromChartData(chartType, data);
          
          if (tableData && tableData.headers && tableData.rows) {
            // Draw a nice container
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            // Add table instead
            const rowHeight = 8;
            const padding = 5;
            const colWidths = [contentWidth * 0.6, contentWidth * 0.4]; // 60/40 split
            
            // Table header
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
            
            // Draw headers
            doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
            doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
            
            yPos += rowHeight + padding;
            
            // Draw rows with alternating background
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            
            // Check if we have too many rows for the current page
            const lineHeight = rowHeight + padding;
            if (yPos + (lineHeight * 1.5 * (data.length + 1)) > pageHeight - footerSpace) {
              doc.addPage();
              addFooter(doc.internal.getNumberOfPages());
              yPos = margin + 10;
              
              // Redraw table header on new page
              doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
              doc.setTextColor(255, 255, 255);
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(9);
              doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
              
              doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
              doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
              
              yPos += rowHeight + padding;
            }
            
            tableData.rows.forEach((row, i) => {
              // Check if we need a new page for this row
              if (yPos > pageHeight - footerSpace) {
                doc.addPage();
                addFooter(doc.internal.getNumberOfPages());
                yPos = margin + 10;
                
                // Redraw table header on new page
                doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9);
                doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
                
                doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
                doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
                
                yPos += rowHeight + padding;
                doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
              }
              
              // Alternating row background
              if (i % 2 === 0) {
                doc.setFillColor(245, 247, 250);
                doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
              }
              
              doc.text(row[0], margin + padding, yPos + padding + 5);
              doc.text(row[1].toString(), margin + colWidths[0], yPos + padding + 5);
              
              yPos += rowHeight + padding;
            });
            
            return yPos + 10; // Add some padding after the table
          } else {
            // Fallback placeholder if no table data
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
              .setFontSize(9)
              .setFont('helvetica', 'italic')
              .text("Données non disponibles pour l'export", margin + contentWidth/2, yPos + height/2, { align: 'center' });
            
            return yPos + height + 10;
          }
        } catch (error) {
          console.error('Error adding chart to PDF:', error);
          
          // Fallback to placeholder
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
          
          doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
            .setFontSize(9)
            .setFont('helvetica', 'italic')
            .text("Graphique non disponible pour l'export", margin + contentWidth/2, yPos + height/2, { align: 'center' });
          
          return yPos + height + 10;
        }
      };
      
      // Add a data table with proper pagination
      const addDataTable = (headers, data, yPos, title = null, estimatedHeight = null) => {
        const footerSpace = 30; // Reserve space for footer
        
        // Calculate an estimated table height if not provided
        if (!estimatedHeight) {
          estimatedHeight = data.length * 10 + 15; // Header + rows + margins
        }
        
        // Check if we need to add a page break before the table
        if (yPos + (title ? 12 : 0) + estimatedHeight > pageHeight - footerSpace) {
          doc.addPage();
          addFooter(doc.internal.getNumberOfPages());
          yPos = margin + 10;
        }
        
        if (title) {
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(10)
            .setFont('helvetica', 'bold')
            .text(title, margin, yPos);
          yPos += 12;
        }
        
        doc.autoTable({
          startY: yPos,
          head: [headers],
          body: data,
          theme: 'grid',
          headStyles: { 
            fillColor: primaryColor, 
            textColor: [255, 255, 255], 
            fontStyle: 'bold' 
          },
          styles: { 
            fontSize: 8, 
            cellPadding: 3 
          },
          alternateRowStyles: { 
            fillColor: [245, 247, 250] 
          },
          margin: { 
            left: margin, 
            right: margin,
            bottom: footerSpace // Important: leave space for footer
          },
          tableWidth: 'auto',
          didDrawPage: function(data) {
            // Add footer to each page
            addFooter(doc.internal.getNumberOfPages());
          },
          // Handle pagination better
          willDrawCell: function(data) {
            const currentY = data.cursor.y;
            
            // If this would go into the footer space, move to next page
            if (currentY + data.row.height > pageHeight - footerSpace) {
              doc.addPage();
              data.cursor.y = margin + 10;
              
              // Re-add the header if this is not the header row
              if (data.row.section !== 'head') {
                // We need to manually set the header as drawn
                data.cursor.y += 0; // Keep position as is
              }
            }
          }
        });
        
        return doc.lastAutoTable.finalY + 10; // Return final position with padding
      };
      
      // ========== Start PDF Generation ==========
      let yPos = addHeader('Rapport des Réservations', 'Analyse détaillée des réservations');
      
      // Add key metrics/KPIs
      yPos = addSection('Indicateurs clés', yPos);
      
      // Operational KPIs
      const kpis = [
        { 
          title: 'Total Réservations', 
          value: totalReservations.toString(),
          change: `${yearOverYearGrowth >= 0 ? '+' : ''}${yearOverYearGrowth.toFixed(1)}%`
        },
        { 
          title: 'Confirmées', 
          value: confirmedReservations.toString(), 
          change: `${totalReservations > 0 ? ((confirmedReservations / totalReservations) * 100).toFixed(1) : 0}%`
        },
        { 
          title: 'En Attente', 
          value: pendingReservations.toString(),
          change: `${totalReservations > 0 ? ((pendingReservations / totalReservations) * 100).toFixed(1) : 0}%`
        },
        { 
          title: 'Annulées', 
          value: cancelledReservations.toString(),
          change: `${totalReservations > 0 ? ((cancelledReservations / totalReservations) * 100).toFixed(1) : 0}%`
        }
      ];
      
      yPos = addKpiRow(kpis, yPos);
      
      // Financial metrics in a second row
      const financialKpis = [
        { 
          title: 'Revenus Totaux', 
          value: formatCurrency(totalRevenue)
        },
        { 
          title: 'Valeur Moyenne', 
          value: formatCurrency(avgReservationValue)
        },
        { 
          title: 'Taux de Conversion', 
          value: `${conversionRate.toFixed(1)}%` 
        },
        { 
          title: 'Taux d\'annulation', 
          value: `${cancellationRate.toFixed(1)}%` 
        }
      ];
      
      yPos = addKpiRow(financialKpis, yPos);
      
      // Add status distribution chart
      yPos = addSection('Répartition des statuts', yPos);
      
      // Prepare status data
      const statusData = prepareStatusData();
      yPos = await addChartToPDF('pie', statusData, 'Statut des réservations', yPos);
      
      // Add status data as a table for clarity
      const statusTableData = statusData.map(item => [
        item.name, 
        item.value.toString(), 
        `${totalReservations > 0 ? ((item.value / totalReservations) * 100).toFixed(1) : 0}%`
      ]);
      
      // Calculate estimated height for status table
      const statusTableEstimatedHeight = statusTableData.length * 10 + 15;
      
      // Check if we need to start a new page for the status table
      if (yPos + 15 + statusTableEstimatedHeight > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addDataTable(['Statut', 'Nombre', 'Pourcentage'], statusTableData, yPos, 'Détails des statuts', statusTableEstimatedHeight);
      
      // Add top destinations chart and table
      if (yPos > pageHeight - 120) { // Reduced from 140 to 120
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSection('Top destinations', yPos);
      const destinationData = prepareDestinationData();
      yPos = await addChartToPDF('bar', destinationData, 'Top 5 destinations', yPos);
      
      // Add destinations data as a table
      const destinationTableData = destinationData.map(item => [
        item.name, item.value.toString()
      ]);
      
      // Calculate estimated height for destination table
      const destinationTableEstimatedHeight = destinationTableData.length * 10 + 15;
      
      // Check if we need a page break before the destination table
      if (yPos + 15 + destinationTableEstimatedHeight > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addDataTable(
        ['Destination', 'Nombre de réservations'], 
        destinationTableData, 
        yPos, 
        'Destinations les plus populaires',
        destinationTableEstimatedHeight
      );
      
      // Add monthly data chart
      if (yPos > pageHeight - 160) { // Reduced from 180 to 160
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSection('Évolution mensuelle', yPos);
      const monthlyData = prepareMonthlyData();
      yPos = await addChartToPDF('bar', monthlyData, 'Réservations par mois', yPos, 100); // Reduced from 140 to 100
      
      // Add revenue chart
      if (yPos > pageHeight - 160) { // Reduced from 180 to 160
        doc.addPage();
        yPos = 20;
      } else {
        yPos += 10; // Add a little spacing if both charts are on the same page
      }
      
      yPos = addSection('Analyse financière', yPos);
      const revenueData = prepareRevenueData();
      yPos = await addChartToPDF('line', revenueData, 'Revenus par mois', yPos, 100); // Reduced from 140 to 100
      
      // Add reservations list - always start on a new page
      doc.addPage();
      yPos = 20;
      yPos = addSection('Liste des réservations', yPos);
      
      // Format reservation data for table
      const reservationsTableData = filteredReservations.map(r => [
        r.reference || r.id,
        r.clientNom || 'N/A',
        r.destination || 'N/A',
        new Date(r.dateDepart).toLocaleDateString('fr-FR'),
        new Date(r.dateRetour).toLocaleDateString('fr-FR'),
        r.statut || 'N/A',
        formatCurrency(r.prixTotal || 0)
      ]);
      
      // If we have many reservations, split them into chunks to avoid memory issues
      const MAX_ROWS_PER_TABLE = 30;
      
      if (reservationsTableData.length <= MAX_ROWS_PER_TABLE) {
        // Add the single table with all reservations
        yPos = addDataTable(
          ['Référence', 'Client', 'Destination', 'Départ', 'Retour', 'Statut', 'Montant'], 
          reservationsTableData,
          yPos,
          'Détails des réservations'
        );
      } else {
        // Split into chunks
        let currentPage = 1;
        const totalPages = Math.ceil(reservationsTableData.length / MAX_ROWS_PER_TABLE);
        
        for (let i = 0; i < reservationsTableData.length; i += MAX_ROWS_PER_TABLE) {
          const chunk = reservationsTableData.slice(i, i + MAX_ROWS_PER_TABLE);
          
          if (i > 0) {
            doc.addPage();
            yPos = 20;
          }
          
          yPos = addDataTable(
            ['Référence', 'Client', 'Destination', 'Départ', 'Retour', 'Statut', 'Montant'], 
            chunk,
            yPos,
            `Détails des réservations (Page ${currentPage} sur ${totalPages})`
          );
          
          currentPage++;
        }
      }
      
      // Final check to ensure no content overlaps with footer
      const footerSpace = 30;
      
      // If the final content element is too close to the footer, add an extra page
      if (yPos > pageHeight - footerSpace) {
        doc.addPage();
      }
      
      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        addFooter(i);
      }
      
      // Add additional diagnostic information to console
      console.log('Reservations PDF generation completed successfully');
      
      try {
        // Save the PDF with formatted filename
        const dateStr = new Date().toISOString().split('T')[0];
        const agencyName = (settings.agencyName || 'Agence').replace(/\s+/g, '_');
        const filename = `${agencyName}_Rapport_Reservations_${dateStr}.pdf`;
        
        // Use the basic save method which is more reliable
        doc.save(filename);
        
        // Show success notification
        setNotification({
          open: true,
          message: `Le rapport a été généré avec succès: ${filename}`,
          severity: 'success'
        });
      } catch (saveError) {
        console.error('Error saving PDF:', saveError);
        setNotification({
          open: true,
          message: 'Erreur lors de l\'enregistrement du PDF. Veuillez réessayer.',
          severity: 'error'
        });
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // More detailed error tracking
      let errorMessage = 'Erreur lors de la génération du PDF.';
      if (error.message) {
        errorMessage += ' Détail: ' + error.message;
      }
      console.log('PDF Generation Error Details:', errorMessage);
      // Use a nicer notification instead of alert
      setNotification({
        open: true,
        message: 'Erreur lors de la génération du PDF. Veuillez réessayer.',
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const navigateToReservation = (reservationId) => {
    navigate(`/reservations/${reservationId}`);
  };

  // Filter reservations based on selected filters
  const filteredReservations = reservations.filter(reservation => {
    // Status filter
    if (statusFilter !== 'all' && reservation.statut !== statusFilter) {
      return false;
    }
    
    // Destination filter
    if (destinationFilter !== 'all' && reservation.destination !== destinationFilter) {
      return false;
    }
    
    // Date filters
    if (dateFilterEnabled && startDate && endDate) {
      const reservationDate = new Date(reservation.dateDepart);
      if (reservationDate < startDate || reservationDate > endDate) {
        return false;
      }
      return true;
    }
    
    if (periodFilter === 'all') return true;
    
    const reservationDate = new Date(reservation.dateDepart);
    const now = new Date();
    
    if (periodFilter === 'month') {
      // Current month
      return reservationDate.getMonth() === now.getMonth() && 
             reservationDate.getFullYear() === now.getFullYear();
    } else if (periodFilter === 'quarter') {
      // Current quarter
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const reservationQuarter = Math.floor(reservationDate.getMonth() / 3);
      return reservationQuarter === currentQuarter && 
             reservationDate.getFullYear() === now.getFullYear();
    } else if (periodFilter === 'year') {
      // Current year
      return reservationDate.getFullYear() === now.getFullYear();
    } else if (periodFilter === 'lastmonth') {
      // Last month
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return reservationDate.getMonth() === lastMonth.getMonth() && 
             reservationDate.getFullYear() === lastMonth.getFullYear();
    }
    
    return true;
  });

  // Prepare data for charts
  const prepareStatusData = () => {
    const statusCounts = { 'Confirmée': 0, 'En attente': 0, 'Annulée': 0 };
    
    filteredReservations.forEach(reservation => {
      if (reservation.statut in statusCounts) {
        statusCounts[reservation.statut]++;
      }
    });
    
    return Object.keys(statusCounts).map(status => ({
      name: status,
      value: statusCounts[status]
    }));
  };

  const prepareDestinationData = () => {
    const destinationCounts = {};
    
    filteredReservations.forEach(reservation => {
      if (reservation.destination) {
        destinationCounts[reservation.destination] = (destinationCounts[reservation.destination] || 0) + 1;
      }
    });
    
    return Object.entries(destinationCounts)
      .map(([destination, count]) => ({ name: destination, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Top 5 destinations
  };

  const prepareMonthlyData = () => {
    const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    const monthlyData = Array(12).fill().map((_, i) => ({ 
      name: monthNames[i], 
      confirmées: 0,
      enAttente: 0, 
      annulées: 0,
      total: 0
    }));
    
    filteredReservations.forEach(reservation => {
      const date = new Date(reservation.dateDepart);
      const month = date.getMonth();
      
      monthlyData[month].total++;
      
      if (reservation.statut === 'Confirmée') {
        monthlyData[month].confirmées++;
      } else if (reservation.statut === 'En attente') {
        monthlyData[month].enAttente++;
      } else if (reservation.statut === 'Annulée') {
        monthlyData[month].annulées++;
      }
    });
    
    return monthlyData;
  };

  const prepareRevenueData = () => {
    const monthNames = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
    const revenueData = Array(12).fill().map((_, i) => ({ 
      name: monthNames[i], 
      revenus: 0
    }));
    
    // Use both reservation data and transaction data for accuracy
    // First add reservation values
    filteredReservations
      .filter(res => res.statut === 'Confirmée')
      .forEach(reservation => {
        const date = new Date(reservation.dateDepart);
        const month = date.getMonth();
        revenueData[month].revenus += (reservation.prixTotal || 0);
      });
      
    // Then incorporate financial transactions for more accuracy
    transactions
      .filter(txn => txn.type === 'Recette' && txn.categorie === 'Reservation')
      .forEach(txn => {
        const date = new Date(txn.date);
        const month = date.getMonth();
        // Don't double count - this is just to ensure we're showing correct data
        // if the transaction is already linked to a filtered reservation
        const linkedReservation = filteredReservations.find(r => r.id === txn.reservationId);
        if (!linkedReservation) {
          revenueData[month].revenus += (txn.montant || 0);
        }
      });
    
    return revenueData;
  };

  // Calculate statistics using real data
  const totalReservations = filteredReservations.length;
  const confirmedReservations = filteredReservations.filter(r => r.statut === 'Confirmée').length;
  const pendingReservations = filteredReservations.filter(r => r.statut === 'En attente').length;
  const cancelledReservations = filteredReservations.filter(r => r.statut === 'Annulée').length;
  
  // Calculate revenue using both reservation and transaction data for accuracy
  const totalRevenue = filteredReservations
    .filter(r => r.statut === 'Confirmée')
    .reduce((sum, r) => sum + (r.prixTotal || 0), 0);
  
  const avgReservationValue = confirmedReservations > 0 ? 
    totalRevenue / confirmedReservations : 0;

  // Calculation for year-over-year growth using real data
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  
  const currentYearReservations = reservations.filter(r => {
    const date = new Date(r.dateDepart);
    return date.getFullYear() === currentYear;
  });
  
  const lastYearReservations = reservations.filter(r => {
    const date = new Date(r.dateDepart);
    return date.getFullYear() === lastYear;
  });
  
  const yearOverYearGrowth = lastYearReservations.length > 0 ? 
    ((currentYearReservations.length - lastYearReservations.length) / lastYearReservations.length) * 100 : 0;
  
  const conversionRate = totalReservations > 0 ? 
    (confirmedReservations / totalReservations) * 100 : 0;
  
  const cancellationRate = totalReservations > 0 ? 
    (cancelledReservations / totalReservations) * 100 : 0;

  // Handle closing notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
        >
          <Link component={RouterLink} to="/tableau-de-bord" color="inherit">
            {t('Tableau de bord')}
          </Link>
          <Link component={RouterLink} to="/reservations" color="inherit">
            {t('Réservations')}
          </Link>
          <Typography color="text.primary">{t('Rapports')}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('Rapport des Réservations')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('Analyse et statistiques des réservations pour une meilleure prise de décision')}
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            onClick={exportToPDF}
            disabled={isExporting}
            sx={{ 
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              minWidth: '180px'
            }}
          >
            {isExporting ? 'Génération...' : 'Télécharger PDF'}
          </Button>
        </Stack>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="period-filter-label">{t('Période')}</InputLabel>
            <Select
              labelId="period-filter-label"
              id="period-filter"
              value={periodFilter}
              label={t('Période')}
              onChange={handlePeriodChange}
            >
              <MenuItem value="all">{t('Toutes les périodes')}</MenuItem>
              <MenuItem value="month">{t('Mois courant')}</MenuItem>
              <MenuItem value="lastmonth">{t('Mois précédent')}</MenuItem>
              <MenuItem value="quarter">{t('Trimestre courant')}</MenuItem>
              <MenuItem value="year">{t('Année courante')}</MenuItem>
              <MenuItem value="custom">{t('Personnalisée')}</MenuItem>
            </Select>
          </FormControl>
          
          {dateFilterEnabled && (
            <>
              <TextField
                label={t("Date de début")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <TextField
                label={t("Date de fin")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
              <Button 
                variant="contained" 
                size="small"
                onClick={handleCustomDateFilter}
              >
                {t('Appliquer')}
              </Button>
            </>
          )}
          
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="status-filter-label">{t('Statut')}</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              label={t('Statut')}
              onChange={handleStatusChange}
            >
              <MenuItem value="all">{t('Tous')}</MenuItem>
              <MenuItem value="Confirmée">{t('Confirmée')}</MenuItem>
              <MenuItem value="En attente">{t('En attente')}</MenuItem>
              <MenuItem value="Annulée">{t('Annulée')}</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="destination-filter-label">{t('Destination')}</InputLabel>
            <Select
              labelId="destination-filter-label"
              id="destination-filter"
              value={destinationFilter}
              label={t('Destination')}
              onChange={handleDestinationChange}
            >
              <MenuItem value="all">{t('Toutes')}</MenuItem>
              {destinations.map(destination => (
                <MenuItem key={destination} value={destination}>{destination}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <div id="reservation-report-content" ref={reportRef}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('Total Réservations')}
                </Typography>
                <Typography variant="h4" component="div">
                  {isLoading ? <CircularProgress size={25} /> : totalReservations}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Chip 
                    label={`${yearOverYearGrowth.toFixed(1)}% ${yearOverYearGrowth >= 0 ? '↑' : '↓'}`}
                    size="small"
                    color={yearOverYearGrowth >= 0 ? 'success' : 'error'}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('vs année précédente')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'success.light' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('Confirmées')}
                </Typography>
                <Typography variant="h4" component="div">
                  {isLoading ? <CircularProgress size={25} /> : confirmedReservations}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {confirmedReservations > 0 && totalReservations > 0 ? 
                    `${((confirmedReservations / totalReservations) * 100).toFixed(1)}% ${t('du total')}` :
                    '0% du total'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('En Attente')}
                </Typography>
                <Typography variant="h4" component="div">
                  {isLoading ? <CircularProgress size={25} /> : pendingReservations}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {pendingReservations > 0 && totalReservations > 0 ? 
                    `${((pendingReservations / totalReservations) * 100).toFixed(1)}% ${t('du total')}` :
                    '0% du total'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', bgcolor: 'error.light' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('Annulées')}
                </Typography>
                <Typography variant="h4" component="div">
                  {isLoading ? <CircularProgress size={25} /> : cancelledReservations}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {cancelledReservations > 0 && totalReservations > 0 ? 
                    `${((cancelledReservations / totalReservations) * 100).toFixed(1)}% ${t('du total')}` :
                    '0% du total'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mb: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{ mb: 2 }}
            >
              <Tab label={t('Aperçu')} />
              <Tab label={t('Tendances')} />
              <Tab label={t('Détails')} />
            </Tabs>
            
            <Divider sx={{ mb: 3 }} />
            
            {/* Tab 1: Overview */}
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>{t('Statut des réservations')}</Typography>
                  <Box sx={{ height: 300 }} id="status-chart">
                    {isLoading ? (
                      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={prepareStatusData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {prepareStatusData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Legend />
                          <RechartsTooltip formatter={(value) => [value, 'Réservations']} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>{t('Top 5 destinations')}</Typography>
                  <Box sx={{ height: 300 }} id="destinations-chart">
                    {isLoading ? (
                      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={prepareDestinationData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip formatter={(value) => [value, 'Réservations']} />
                          <Bar dataKey="value" fill="#8884d8">
                            {prepareDestinationData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{t('Revenus')}</Typography>
                      <Typography variant="h4" component="div" sx={{ mb: 2 }}>
                        {isLoading ? (
                          <CircularProgress size={25} />
                        ) : (
                          formatCurrency(totalRevenue)
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('Valeur moyenne par réservation confirmée')}: {isLoading ? '...' : formatCurrency(avgReservationValue)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{t('Taux de Conversion')}</Typography>
                      <Typography variant="h4" component="div" sx={{ mb: 2 }}>
                        {isLoading ? <CircularProgress size={25} /> : `${conversionRate.toFixed(1)}%`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('Réservations confirmées vs total')}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{t('Taux d\'annulation')}:</span>
                          <span>{cancellationRate.toFixed(1)}%</span>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Tab 2: Trends */}
            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>{t('Réservations par mois')}</Typography>
                  <Box sx={{ height: 350 }} id="monthly-chart">
                    {isLoading ? (
                      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={prepareMonthlyData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="confirmées" name={t('Confirmées')} fill="#10b981" />
                          <Bar dataKey="enAttente" name={t('En Attente')} fill="#f59e0b" />
                          <Bar dataKey="annulées" name={t('Annulées')} fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>{t('Revenus par mois (réservations confirmées)')}</Typography>
                  <Box sx={{ height: 350 }} id="revenue-chart">
                    {isLoading ? (
                      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={prepareRevenueData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip formatter={(value) => [formatCurrency(value), 'Revenus']} />
                          <Area type="monotone" dataKey="revenus" name={t('Revenus')} stroke="#8884d8" fill="#8884d8" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Tab 3: Details */}
            {currentTab === 2 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Référence')}</TableCell>
                      <TableCell>{t('Client')}</TableCell>
                      <TableCell>{t('Destination')}</TableCell>
                      <TableCell>{t('Date Départ')}</TableCell>
                      <TableCell>{t('Date Retour')}</TableCell>
                      <TableCell>{t('Statut')}</TableCell>
                      <TableCell align="right">{t('Montant')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : filteredReservations.length > 0 ? (
                      filteredReservations.map((reservation) => (
                        <TableRow 
                          key={reservation.id}
                          hover
                          onClick={() => navigateToReservation(reservation.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{reservation.reference || reservation.id}</TableCell>
                          <TableCell>{reservation.clientNom}</TableCell>
                          <TableCell>{reservation.destination}</TableCell>
                          <TableCell>
                            {new Date(reservation.dateDepart).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            {new Date(reservation.dateRetour).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={reservation.statut}
                              size="small"
                              sx={{ 
                                bgcolor: 
                                  reservation.statut === 'Confirmée' ? 'success.light' : 
                                  reservation.statut === 'En attente' ? 'warning.light' : 
                                  'error.light',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(reservation.prixTotal || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">{t('Aucune réservation trouvée')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </div>
    </Container>
  );
};

export default ReservationsReports; 